import { useCallback, useEffect, useState } from "react";

import { integrationApiInstance } from "../apis/integrationApi";
import type { Agent } from "../types/integration";

/**
 * The MSP's agents, shared across every component that needs them.
 *
 * The sidebar, the topbar title and the agent pages all want the same roster, so this keeps one
 * module-level cache per MSP rather than letting each caller fetch. Creating or removing an agent
 * calls `refresh()`, which re-reads once and notifies every subscriber, so the sidebar updates
 * without a page reload.
 *
 * Mirrors the cache shape of useUserMsp, which solves the same problem for the signed-in user.
 */

interface AgentCacheEntry {
  agents: Agent[];
  error: string | null;
  loaded: boolean;
  promise: Promise<Agent[]> | null;
}

const agentCache = new Map<number, AgentCacheEntry>();
const subscribers = new Map<number, Set<() => void>>();

function notify(mspId: number): void {
  subscribers.get(mspId)?.forEach((listener) => listener());
}

function entryFor(mspId: number): AgentCacheEntry {
  const existing = agentCache.get(mspId);
  if (existing) return existing;
  const fresh: AgentCacheEntry = { agents: [], error: null, loaded: false, promise: null };
  agentCache.set(mspId, fresh);
  return fresh;
}

async function loadAgents(mspId: number): Promise<Agent[]> {
  const entry = entryFor(mspId);
  // An in-flight request is shared: four components mounting together must not make four calls.
  if (entry.promise) return entry.promise;
  const promise = (async () => {
    try {
      // The MSP comes from the caller's token server-side, so this takes no argument.
      const response = await integrationApiInstance.getAgents();
      const agents = Array.isArray(response?.rows) ? response.rows : [];
      agentCache.set(mspId, { agents, error: null, loaded: true, promise: null });
      return agents;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load agents";
      // Keep whatever was already loaded: a failed refresh should not blank the sidebar.
      const previous = agentCache.get(mspId)?.agents ?? [];
      agentCache.set(mspId, { agents: previous, error: message, loaded: true, promise: null });
      return previous;
    } finally {
      notify(mspId);
    }
  })();
  agentCache.set(mspId, { ...entryFor(mspId), promise });
  return promise;
}

/** Drop the cache so the next `useAgents` read re-fetches. Call after a create, edit or remove. */
export function invalidateAgents(mspId?: number): void {
  if (mspId === undefined) {
    const ids = Array.from(agentCache.keys());
    agentCache.clear();
    ids.forEach(notify);
    return;
  }
  agentCache.delete(mspId);
  notify(mspId);
}

export interface UseAgentsResult {
  agents: Agent[];
  /** The built-in Theo Technician Agent, or null before the roster has loaded. */
  defaultAgent: Agent | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useAgents(mspId: number | undefined): UseAgentsResult {
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!mspId) return undefined;
    const listener = () => forceRender((tick) => tick + 1);
    const listeners = subscribers.get(mspId) ?? new Set<() => void>();
    listeners.add(listener);
    subscribers.set(mspId, listeners);
    if (!agentCache.get(mspId)?.loaded) void loadAgents(mspId);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) subscribers.delete(mspId);
    };
  }, [mspId]);

  const refresh = useCallback(async () => {
    if (!mspId) return;
    agentCache.delete(mspId);
    await loadAgents(mspId);
  }, [mspId]);

  const entry = mspId ? agentCache.get(mspId) : undefined;
  const agents = entry?.agents ?? [];
  return {
    agents,
    defaultAgent: agents.find((agent) => agent.is_default) ?? null,
    isLoading: Boolean(mspId) && !entry?.loaded,
    error: entry?.error ?? null,
    refresh,
  };
}
